import React, { useEffect, useContext, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ColDef } from "ag-grid-community";
import {
  Calendar,
  CalendarCheck2,
  Download,
  FileCheck2,
  Headset,
  MapPin,
  Receipt,
  ShieldCheck,
  Users,
} from "lucide-react";

import CountCard from "../components/countCard";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import staticDataDashboardSuccess from "../context/actions/auth/staticDataDashboardSuccess";
import NFTPriceBarChar from "../components/timeOrderGraph/NFTPriceBarChar";
import AgGridTable from "../components/ui/AgGridTable";

export default () => {
  const location = useLocation();
  const searchParam = new URLSearchParams(location.search);
  const { authState, staticDataState, staticDataDispatch, handleError } =
    useContext(GlobalContext);

  const [activeMode, setActiveMode] = useState<"live" | "skeleton">("live");

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const dashboard = staticDataState.dashboard;

  const [statCards, setStatCards] = useState<any[]>([]);

  useEffect(() => {
    setStatCards([
      {
        id: "users",
        to: "/users/1",
        title: "Users",
        count: dashboard?.total_users ?? 0,
        currency: "",
        icon: <Users className="w-4 h-4 text-emerald-600" />,
        badgeText: "Active",
        badgeType: "trend-up",
        subtitle: "Total registered accounts",
        iconBgClass: "bg-emerald-50 text-emerald-600 border-emerald-100/80",
      },
      {
        id: "bookings",
        to: "/booked-rides/1",
        title: "Bookings",
        count: dashboard?.total_bookings ?? 0,
        currency: "",
        icon: <CalendarCheck2 className="w-4 h-4 text-emerald-600" />,
        badgeText: "Active",
        badgeType: "trend-up",
        subtitle: "Trips requested",
        iconBgClass: "bg-emerald-50 text-emerald-600 border-emerald-100/80",
      },
      {
        id: "transactions",
        to: "/transactions/1",
        title: "Total Transactions",
        count: dashboard?.total_transactions
          ? Number(dashboard?.total_transactions).toFixed(0)
          : 0,
        currency: "",
        icon: <Receipt className="w-4 h-4 text-emerald-600" />,
        badgeText: "Active",
        badgeType: "trend-up",
        subtitle: "Captured & settled",
        iconBgClass: "bg-emerald-50 text-emerald-600 border-emerald-100/80",
      },
      {
        id: "contact-us",
        to: "/contact-us/1",
        title: "Contact Us",
        count: dashboard?.total_contacts ?? 0,
        currency: "",
        icon: <Headset className="w-4 h-4 text-emerald-600" />,
        badgeText: "Review",
        badgeType: "trend-up",
        subtitle: "Active tickets in queue",
        iconBgClass: "bg-emerald-50 text-emerald-600 border-emerald-100/80",
      },
      {
        id: "doc-verification",
        to: "/users/1?document_verified=0",
        title: "Doc Verification",
        count: dashboard?.total_docs_verified ?? 0,
        currency: "",
        icon: <ShieldCheck className="w-4 h-4 text-emerald-600" />,
        badgeText: "Review",
        badgeType: "trend-up",
        subtitle: "drivers awaiting ID check",
        iconBgClass: "bg-emerald-50 text-emerald-600 border-emerald-100/80",
      },
    ]);
  }, [dashboard]);

  const dashBoard = async () => {
    try {
      const dbData = await henceforthApi.Dashboard.getPagnation(
        searchParam.get("type")
      );
      staticDataDashboardSuccess(dbData.data)(staticDataDispatch);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    dashBoard();
  }, [searchParam.get("type")]);

  const CONTENT_TYPE = "AUDIO";

  // AG Grid Column Definitions for Recent Bookings
  const bookingColDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        width: 70,
        valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Passenger",
        field: "user_id.name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const res = p.data;
          const imgSrc =
            res?.content_type === CONTENT_TYPE
              ? `https://ipfs.infura.io/ipfs/${res?.cover_img}`
              : res?.user_id?.profile_pic && res?.user_id?.profile_pic !== "null"
                ? henceforthApi.FILES.imageOriginal(res?.user_id?.profile_pic)
                : profile_placeholder;

          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={imgSrc}
                alt=""
                className="w-7 h-7 rounded-full object-cover border border-slate-200"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <span className="font-semibold text-slate-900 text-xs truncate">
                {res?.user_id?.name || "Cooldude"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Route",
        minWidth: 200,
        flex: 1.8,
        cellRenderer: (p: any) => {
          const res = p.data;
          const from =
            res?.ride_id?.address_from ||
            res?.pickup_address ||
            res?.address_from ||
            "-";
          const to =
            res?.ride_id?.address_to ||
            res?.dropoff_address ||
            res?.address_to ||
            "-";

          return (
            <div className="flex flex-col justify-center py-1 gap-1 text-xs text-slate-700 min-w-0 w-full leading-tight">
              <div className="flex items-center gap-1.5 truncate" title={from}>
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate font-medium">{from}</span>
              </div>
              <div className="flex items-center gap-1.5 truncate text-slate-500" title={to}>
                <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
                <span className="truncate">{to}</span>
              </div>
            </div>
          );
        },
      },
    ];
  }, []);

  // AG Grid Column Definitions for Recent Users
  const userColDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        width: 70,
        valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "User",
        field: "name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const res = p.data;
          const name = res?.name || "Komal Dhiman";
          const firstChar = name.charAt(0).toUpperCase();
          const hasImage = res?.profile_pic && res?.profile_pic !== "null";

          return (
            <div className="flex items-center gap-2.5 py-1">
              {hasImage ? (
                <img
                  src={henceforthApi.FILES.imageOriginal(res.profile_pic)}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover border border-slate-200"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = profile_placeholder;
                  }}
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-[11px] shrink-0">
                  {firstChar}
                </div>
              )}
              <span className="font-semibold text-slate-900 text-xs truncate">
                {name}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 170,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const val = p.value || p.data?.social_token || "user@example.com";
          return (
            <span className="text-slate-500 text-xs truncate" title={val}>
              {val}
            </span>
          );
        },
      }
    ];
  }, []);

  const recentBookingsData = dashboard?.recent_bookings?.slice(0, 5) || [];
  const recentUsersData = dashboard?.recent_users?.slice(0, 5) || [];

  // Formatted date string for top-right banner
  const todayDateStr = useMemo(() => {
    const d = new Date();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `Today: ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }, []);

  return (
    <div className="space-y-6">
      {/* Top Banner: Dashboard Overview + Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-1">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Dashboard Overview
            </h1>

          </div>
          <p className="text-xs text-slate-500 mt-1">
            Real-time ride volume, system transactions, and fleet compliance telemetry.
          </p>
        </div>

      </div>

      {/* 5 Top Stat Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card) => (
          <Link key={card.id} to={card.to} className="no-underline block">
            <CountCard
              title={card.title}
              count={card.count}
              currency={card.currency}
              icon={card.icon}
              badgeText={card.badgeText}
              badgeType={card.badgeType}
              subtitle={card.subtitle}
              iconBgClass={card.iconBgClass}
            />
          </Link>
        ))}
      </div>

      {/* Analytics Chart Card: Time vs Sales */}
      <NFTPriceBarChar />

      {/* Recent Activity 2-Column Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Recent Booked Rides Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden flex flex-col p-4 sm:p-5">
          <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-900 leading-tight">
                  Recent Booked Rides
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                  5 Active
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Latest 5 ride bookings in the platform
              </p>
            </div>

            <Link
              to="/booked-rides/1"
              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <span>View All</span>
            </Link>
          </div>

          <div className="flex-1">
            <AgGridTable
              rowData={recentBookingsData}
              columnDefs={bookingColDefs}
              domLayout="autoHeight"
              rowHeight={58}
              headerHeight={40}
              emptyMessage="No recent bookings found"
            />
          </div>
        </div>

        {/* Recent Users Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden flex flex-col p-4 sm:p-5">
          <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-slate-900 leading-tight">
                  Recent Users
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">
                  5 New
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Newly registered users on ReadyShare
              </p>
            </div>

            <Link
              to="/users/1"
              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100"
            >
              <span>View All</span>
            </Link>
          </div>

          <div className="flex-1">
            <AgGridTable
              rowData={recentUsersData}
              columnDefs={userColDefs}
              domLayout="autoHeight"
              rowHeight={58}
              headerHeight={40}
              emptyMessage="No recent users found"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
